import React, { useEffect, useState, useMemo, useRef, Fragment } from 'react';
import styled from 'styled-components';
import T from 'components/common/text/T';
import AppModal from 'components/common/modal/AppModal';
import PlainModal from 'components/common/modal/PlainModal';
import SoftwarePathModalContainer from 'containers/user/SoftwarePathModalContainer';
import RemakeTypeEditModalContainer from 'containers/user/RemakeTypeEditModalContainer';
import UserCustomObjectModalContainer from 'containers/user/UserCustomObjectModalContainer';
import {
  icon_scanimport,
  icon_workspace,
  icon_software,
  icon_left_arrow_blue,
  icon_remaketype,
  icon_indication,
  icon_material,
  icon_system_setting,
  icon_cloud_manage,
} from 'components/base/images';
import { useTranslation } from 'react-i18next';
import { directoryTypeList } from 'lib/mapper';
import { PageSubTitle } from 'components/base/title/PageTitle';
import SystemSettingModalContainer from './SystemSettingModalContainer';
import CloudManagementModalContainer from './CloudManagementModalContainer';
import { useCloudManageStore } from 'store/local/cloudManage';
import { DashboardActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';

function SoftwareSettingContainer() {
  const {
    productData: {
      dataInfo: { productName, totalSize, totalUsage },
    },
  }: {
    productData: {
      dataInfo: {
        isExpired: number;
        productName: string;
        totalSize: number;
        totalUsage: number;
      };
    };
  } = useShallowAppSelector(state => ({
    productData: state.dashboard.productData.data || {
      dataInfo: {
        isExpired: 0,
        productName: 'Basic',
        totalSize: 5,
        totalUsage: 0,
      },
    },
  }));

  const used = Math.ceil(Number(totalUsage) * 100) / 100;
  const total = Math.ceil(Number(totalSize) * 100) / 100;

  const { t } = useTranslation();
  const WORKSPACE = 'WORKSPACE';
  const SCANWORKSPACE = 'SCAN_WORKSPACE';
  const SOFTWARE = 'SOFTWARE';
  const REMAKE = 'REMAKE';
  const INDICATION = 'INDICATION';
  const MATERIAL = 'MATERIAL';
  const SYSTEMS = 'SYSTEMS';
  const CLOUD = 'CLOUD';
  const [modalType, setModalType] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const profileRef = useRef<null | any>(null);
  const { ipcRenderer } = window;
  const [dirList, setDirList] = useState<any[]>([]);
  const { cloudManageOpen } = useCloudManageStore();

  const handleOpenModal = (type: string | any) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenWorkspace = (directoryType: string) => {
    if (ipcRenderer) {
      ipcRenderer.send('openDirectory', { directoryType });
    }
  };

  const handleGetWorkspace = () => {
    if (ipcRenderer) {
      ipcRenderer.send('directoryGet', {
        directoryTypeList: [WORKSPACE, SCANWORKSPACE],
      });
    }
  };

  const handleDirectoryAdd = (directoryType: string, directory: string) => {
    ipcRenderer.send('directoryAdd', { directoryType, directory });
  };

  useEffect(() => {
    if (!ipcRenderer) return;
    handleGetWorkspace();
    ipcRenderer.on('directoryGetRes', (e: any, data: object[]) => {
      const dirPath = directoryTypeList.map((dirType: any) => {
        const returnVal = data.find((item: any) => item.directoryType === dirType.type);
        return { ...returnVal };
      }, []);
      setDirList([...dirPath]);
    });

    return () => {
      setDirList([]);
    };
  }, []);

  useEffect(() => {
    if (!ipcRenderer) return;
    ipcRenderer.on(
      'openDirectoryRes',
      (e: any, data: { directoryType: string; canceled: boolean; filePaths: string[] }) => {
        if (!!data.canceled) return;
        if (data.directoryType === WORKSPACE || data.directoryType === SCANWORKSPACE) {
          handleDirectoryAdd(data.directoryType, data.filePaths[0]);
        }
      },
    );
  }, []);

  useEffect(() => {
    if (!ipcRenderer) return;
    ipcRenderer.on(
      'directoryAddRes',
      (e: any, data: { status: boolean; directoryType: string; directory: string }) => {
        if (data.directoryType === WORKSPACE || data.directoryType === SCANWORKSPACE) {
          handleGetWorkspace();
        }
      },
    );
  }, []);

  useEffect(() => {
    if (!cloudManageOpen) return;
    handleOpenModal(CLOUD);
  }, [cloudManageOpen]);

  useEffect(() => {
    DashboardActions.fetch_product_data_request();
  }, []);

  const modalList = [
    {
      type: SOFTWARE,
      title: t('GLOBAL_SOFTWARE_SETTING'),
      component: <SoftwarePathModalContainer onCloseModal={handleCloseModal} />,
      width: 1280,
    },
    {
      type: REMAKE,
      title: t('GLOBAL_REMAKE_TYPE'),
      component: <RemakeTypeEditModalContainer onCloseModal={handleCloseModal} />,
      width: 1280,
    },
    {
      type: INDICATION,
      title: t('GLOBAL_USER_INDICATION'),
      component: (
        <UserCustomObjectModalContainer onCloseModal={handleCloseModal} type={INDICATION} />
      ),

      width: 1000,
    },
    {
      type: MATERIAL,
      title: t('GLOBAL_USER_MATERIAL'),
      component: <UserCustomObjectModalContainer onCloseModal={handleCloseModal} type={MATERIAL} />,
      width: 1000,
    },
    {
      type: SYSTEMS,
      title: t('GLOBAL_SYSTEM_SETTINGS'),
      component: <SystemSettingModalContainer onCloseModal={handleCloseModal} />,
      width: 1000,
    },
    {
      type: CLOUD,
      title: t('GLOBAL_CLOUD_STORAGE'),
      component: <CloudManagementModalContainer onCloseModal={handleCloseModal} />,
      width: 1420,
    },
  ];

  return (
    <SoftwareSetting data-component-name="SoftwareSetting">
      {useMemo(() => {
        const findData = modalList.find(item => item.type === modalType);
        return (
          <PlainModal isOpen={isModalOpen} onClose={handleCloseModal} width={findData?.width}>
            <AppModal
              title={findData?.title}
              content={findData?.component}
              contentCardStyle={{
                padding: '0 0 28px',
                backgroundColor: 'transparent',
              }}
              isCloseIcon={true}
              onClick={handleCloseModal}
              onCancel={handleCloseModal}
              hideButton={true}
            />
          </PlainModal>
        );
      }, [isModalOpen])}
      {/* <div className="softwareSetting__title_box" ref={profileRef}>
        <h1 className="SoftwareSetting__title">
          <T>GLOBAL_SETTING</T>
        </h1>
      </div> */}
      <PageSubTitle title={t('GLOBAL_SETTING')} />

      <div className="SoftwareSetting__button_container">
        {/* Remake Type */}
        <SettingButton
          title={t('GLOBAL_REMAKE_TYPE')}
          subTitle={t('GLOBAL_REMAKE_TYPE_EDIT')}
          icon={icon_remaketype}
          onClick={() => {
            handleOpenModal(REMAKE);
          }}
        />
        {/* Custom Indiaction */}
        <SettingButton
          title={t('GLOBAL_USER_INDICATION')}
          subTitle={t('GLOBAL_USER_INDICATION_EDIT')}
          icon={icon_indication}
          onClick={() => {
            handleOpenModal(INDICATION);
          }}
        />
        {/* Custom Material */}
        <SettingButton
          title={t('GLOBAL_USER_MATERIAL')}
          subTitle={t('GLOBAL_USER_MATERIAL_EDIT')}
          icon={icon_material}
          onClick={() => {
            handleOpenModal(MATERIAL);
          }}
        />

        {/* Software */}
        <SettingButton
          title={t('GLOBAL_SOFTWARE')}
          subTitle={t('FILE_LOCATION')}
          icon={icon_software}
          onClick={() => {
            handleOpenModal(SOFTWARE);
          }}
        />

        {/* Workspace */}
        <SettingButton
          title={t('GLOBAL_WORKSPACE')}
          subTitle={
            !ipcRenderer ? (
              'Not available in browser'
            ) : !!dirList?.find((item: any) => item.directoryType === WORKSPACE)?.directory ? (
              dirList?.find((item: any) => item.directoryType === WORKSPACE)?.directory
            ) : (
              <T>SETTING_DIRECTORY</T>
            )
          }
          icon={icon_scanimport}
          onClick={() => {
            if (!ipcRenderer) return;
            handleOpenWorkspace(WORKSPACE);
          }}
        />

        {/* File Import */}
        <SettingButton
          title={t('FILE_IMPORT')}
          subTitle={
            !ipcRenderer ? (
              'Not available in browser'
            ) : !!dirList?.find((item: any) => item.directoryType === SCANWORKSPACE)?.directory ? (
              dirList?.find((item: any) => item.directoryType === SCANWORKSPACE)?.directory
            ) : (
              <T>SETTING_DIRECTORY</T>
            )
          }
          icon={icon_workspace}
          onClick={() => {
            if (!ipcRenderer) return;
            handleOpenWorkspace(SCANWORKSPACE);
          }}
        />

        {/* System Setting */}
        <SettingButton
          title={t('GLOBAL_SYSTEM_SETTINGS')}
          subTitle={t('GLOBAL_SOFTWARE_SETTING')}
          icon={icon_system_setting}
          onClick={() => {
            handleOpenModal(SYSTEMS);
          }}
        />

        {/* Cloud Management */}
        <SettingButton
          title={t('GLOBAL_CLOUD_STORAGE')}
          // subTitle={t('GLOBAL_CLOUD_STORAGE')}
          subTitle={`${used} GB / ${total} GB`}
          icon={icon_cloud_manage}
          onClick={() => {
            handleOpenModal(CLOUD);
          }}
        />
      </div>
    </SoftwareSetting>
  );
}

export interface SettingButtonProps {
  title: string;
  subTitle: string;
  icon: string;
  onClick: () => void;
}
const SettingButton = ({ title, subTitle, icon, onClick = () => {} }: SettingButtonProps) => {
  return (
    <Styled.SettingButton data-component-name="SettingButton" onClick={onClick}>
      <img className="SettingButton__icon" src={icon} />
      <div className="SettingButton__detail">
        <p className="SettingButton__name">{title}</p>
        <p className="SettingButton__path">{subTitle}</p>
      </div>
      <img className="icon_arrow_right" src={icon_left_arrow_blue} />
    </Styled.SettingButton>
  );
};

// const SettingButton = styled.div<{}>``;
const Styled = {
  SettingButton: styled.div`
    width: 515px;
    height: 130px;
    box-shadow: 0px 0px 10px rgb(0 0 0 / 16%);
    border-radius: 10px;
    /* padding: 35px; */
    padding: 0 40px;
    display: flex;
    align-items: center;
    background-color: #fff;
    &:hover {
      cursor: pointer;
    }

    .SettingButton__icon {
      width: 50px;
      height: 50px;
    }
    .SettingButton__detail {
      width: 310px;
      margin: 0 30px;
      .SettingButton__name {
        margin-bottom: 8px;
        font-size: 22px;
        font-weight: 500;
        color: 222;
      }
      .SettingButton__path {
        font-size: 14px;
        font-weight: 400;
        color: #888;
        width: 310px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    .icon_arrow_right {
      font-size: 50px;
      color: #00a6e2;
      font-weight: 400;
      width: 15px;
    }
  `,
};
const SoftwareSetting = styled.section<{}>`
  margin-top: 50px;
  /* padding-top: 30px; */
  .softwareSetting__title_box {
    .SoftwareSetting__title {
      font-size: 26px;
      font-weight: 500;
    }
  }
  .SoftwareSetting__button_container {
    /* margin-top: 30px; */
    display: flex;
    /* justify-content: space-between; */
    justify-content: flex-start;
    flex-wrap: wrap;
    row-gap: 30px;
    column-gap: 30px;
    .SoftwareSetting__button_box {
      width: 515px;
      height: 130px;
      box-shadow: 0px 0px 10px rgb(0 0 0 / 16%);
      border-radius: 10px;
      /* padding: 35px; */
      padding: 0 40px;
      display: flex;
      align-items: center;
      background-color: #fff;

      &:hover {
        cursor: pointer;
      }
      .SoftwareSetting__info_box {
        display: flex;
      }
      .SoftwareSetting__button_icon {
        width: 50px;
        height: 50px;
      }
      .SoftwareSetting__button_detail {
        width: 310px;
        margin: 0 30px;
        .SoftwareSetting__button_name {
          margin-bottom: 8px;
          font-size: 22px;
          font-weight: 500;
          color: 222;
        }
        .SoftwareSetting__button_path {
          font-size: 14px;
          font-weight: 400;
          color: #888;
          width: 310px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .icon_arrow_right {
        font-size: 50px;
        color: #00a6e2;
        font-weight: 400;
        width: 15px;
      }
    }
  }
`;
export default React.memo(SoftwareSettingContainer);
